import { ChangeEvent, KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import InputBase from '@mui/material/InputBase';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import FunctionsRoundedIcon from '@mui/icons-material/FunctionsRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SpaceBarRoundedIcon from '@mui/icons-material/SpaceBarRounded';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';
import {
  getActiveAttributeSearch,
  insertFormulaText,
  legacyConcatenationToFormula,
  parseConcatenationFormula,
  replaceActiveAttributeSearch,
} from '../utils/concatenationFormula';

export interface ConcatenationAttributeOption {
  label: string;
  value: string | number;
  persistentName?: string | null;
}

interface ConcatenationFormulaEditorProps {
  value: string;
  attributeOptions: ConcatenationAttributeOption[];
  onChange: (value: string) => void;
  onValidationChange: (error: string | null) => void;
}

interface FormulaHighlightPart {
  color: string;
  value: string;
  weight?: number;
}

function getFormulaHighlightParts(formula: string): FormulaHighlightPart[] {
  return formula
    .split(/(\{[^}]*\})/g)
    .filter(Boolean)
    .map((part) => {
      if (part.startsWith('{')) return { value: part, color: '#1976D2', weight: 600 };
      return { value: part, color: '#344054' };
    });
}

function getUniqueAttributeOptions(options: ConcatenationAttributeOption[]) {
  const uniqueOptions = new Map<string, ConcatenationAttributeOption>();
  options.forEach((option) => {
    if (option.label && !uniqueOptions.has(option.label.toLowerCase())) {
      uniqueOptions.set(option.label.toLowerCase(), option);
    }
  });
  return Array.from(uniqueOptions.values()).sort((first, second) => first.label.localeCompare(second.label));
}

export function ConcatenationFormulaEditor({
  value,
  attributeOptions,
  onChange,
  onValidationChange,
}: ConcatenationFormulaEditorProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const formulaBarRef = useRef<HTMLDivElement>(null);
  const lastCommittedValueRef = useRef(value);
  const [formula, setFormula] = useState(() => legacyConcatenationToFormula(value));
  const [cursorPosition, setCursorPosition] = useState(formula.length);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(0);
  const [suggestionsDismissed, setSuggestionsDismissed] = useState(false);

  const options = useMemo(() => getUniqueAttributeOptions(attributeOptions), [attributeOptions]);
  const attributeLabels = useMemo(() => options.map((option) => option.label), [options]);
  const formulaResult = useMemo(
    () => parseConcatenationFormula(formula, attributeLabels),
    [attributeLabels, formula]
  );
  const activeAttributeSearch = useMemo(
    () => getActiveAttributeSearch(formula, cursorPosition),
    [cursorPosition, formula]
  );
  const filteredOptions = useMemo(() => {
    if (!activeAttributeSearch) return [];
    const query = activeAttributeSearch.query.trim().toLowerCase();
    return options.filter((option) => !query || option.label.toLowerCase().includes(query)).slice(0, 8);
  }, [activeAttributeSearch, options]);
  const highlightParts = useMemo(() => getFormulaHighlightParts(formula), [formula]);
  const showSuggestions = !suggestionsDismissed && Boolean(activeAttributeSearch) && filteredOptions.length > 0;

  useEffect(() => {
    if (value === lastCommittedValueRef.current) return;
    const nextFormula = legacyConcatenationToFormula(value);
    lastCommittedValueRef.current = value;
    setFormula(nextFormula);
    setCursorPosition(nextFormula.length);
  }, [value]);

  useEffect(() => {
    onValidationChange(formulaResult.error);
  }, [formulaResult.error, onValidationChange]);

  useEffect(() => {
    setSelectedSuggestionIndex(0);
  }, [activeAttributeSearch?.query]);

  const focusAt = (position: number) => {
    setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.setSelectionRange(position, position);
    }, 0);
  };

  const updateFormula = (nextFormula: string, nextCursor: number) => {
    setFormula(nextFormula);
    setCursorPosition(nextCursor);
    setSuggestionsDismissed(false);

    const result = parseConcatenationFormula(nextFormula, attributeLabels);
    onValidationChange(result.error);
    if (!result.error) {
      lastCommittedValueRef.current = result.legacyValue;
      onChange(result.legacyValue);
    } else if (!nextFormula) {
      lastCommittedValueRef.current = '';
      onChange('');
    }
  };

  const handleFormulaChange = (event: ChangeEvent<HTMLInputElement>) => {
    updateFormula(event.target.value, event.target.selectionStart ?? event.target.value.length);
  };

  const insertAttribute = (attribute: string) => {
    const insertion = replaceActiveAttributeSearch(formula, cursorPosition, attribute);
    updateFormula(insertion.formula, insertion.cursor);
    setSuggestionsDismissed(true);
    focusAt(insertion.cursor);
  };

  const handleFormulaKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setSelectedSuggestionIndex((current) => (current + 1) % filteredOptions.length);
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSelectedSuggestionIndex((current) => (current + filteredOptions.length - 1) % filteredOptions.length);
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      insertAttribute(filteredOptions[selectedSuggestionIndex].label);
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      setSuggestionsDismissed(true);
    }
  };

  const insertAtCursor = (text: string, cursorOffset?: number) => {
    const insertion = insertFormulaText(formula, cursorPosition, text, cursorOffset);
    updateFormula(insertion.formula, insertion.cursor);
    focusAt(insertion.cursor);
  };

  return (
    <Stack spacing={1.5} sx={{ mt: 2 }}>
      <Box>
        <Typography variant='input-label' fontWeight={700}>
          Concatenation formula
        </Typography>
        <Typography variant='body2' color='text.secondary' sx={{ mt: 0.25 }}>
          Insert attributes in curly brackets, then type text and separators exactly as they should appear.
        </Typography>
      </Box>

      <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
        <Button
          variant='outlined'
          size='small'
          startIcon={<AddRoundedIcon />}
          onClick={() => insertAtCursor('{', 1)}
          sx={{ textTransform: 'none', borderColor: '#00B3DC', color: '#087EA4' }}
        >
          Attribute
        </Button>
        <Button
          variant='outlined'
          size='small'
          startIcon={<SpaceBarRoundedIcon />}
          onClick={() => insertAtCursor(' ')}
          sx={{ textTransform: 'none', borderColor: '#00B3DC', color: '#087EA4' }}
        >
          Space
        </Button>
      </Stack>

      <Box
        ref={formulaBarRef}
        sx={{
          display: 'flex',
          minHeight: 58,
          border: '1px solid',
          borderColor: formulaResult.error ? 'error.main' : '#98A2AE',
          borderRadius: 1,
          overflow: 'hidden',
          bgcolor: '#fff',
          '&:focus-within': {
            borderColor: formulaResult.error ? 'error.main' : '#00B3DC',
            boxShadow: formulaResult.error ? undefined : '0 0 0 1px #00B3DC',
          },
        }}
      >
        <Box
          sx={{
            width: 58,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRight: '1px solid #D0D5DD',
            bgcolor: '#F9FAFB',
            color: '#3B4797',
            flexShrink: 0,
          }}
        >
          <FunctionsRoundedIcon aria-hidden='true' />
        </Box>

        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', flex: 1, overflow: 'hidden' }}>
          <Box
            aria-hidden='true'
            sx={{
              position: 'absolute',
              left: 14,
              right: 14,
              whiteSpace: 'pre',
              overflow: 'hidden',
              pointerEvents: 'none',
              fontFamily: 'monospace',
              fontSize: 16,
              lineHeight: '24px',
            }}
          >
            {formula ? (
              highlightParts.map((part, index) => (
                <Box
                  component='span'
                  key={`${part.value}-${index}`}
                  sx={{ color: part.color, fontWeight: part.weight }}
                >
                  {part.value}
                </Box>
              ))
            ) : (
              <Box component='span' sx={{ color: '#98A2B3' }}>
                {'{Activity}: {Activity Name}'}
              </Box>
            )}
          </Box>
          <InputBase
            inputRef={inputRef}
            fullWidth
            value={formula}
            onChange={handleFormulaChange}
            inputProps={{
              'aria-label': 'Concatenation formula',
              'aria-autocomplete': 'list',
              'aria-expanded': showSuggestions,
              spellCheck: false,
              onKeyDown: handleFormulaKeyDown,
              onClick: () => {
                setCursorPosition(inputRef.current?.selectionStart ?? formula.length);
                setSuggestionsDismissed(false);
              },
              onKeyUp: () => setCursorPosition(inputRef.current?.selectionStart ?? formula.length),
              onFocus: () => setSuggestionsDismissed(false),
            }}
            sx={{
              px: 1.75,
              '& input': {
                fontFamily: 'monospace',
                fontSize: 16,
                lineHeight: '24px',
                color: 'transparent',
                caretColor: '#101828',
                WebkitTextFillColor: 'transparent',
              },
            }}
          />
        </Box>
      </Box>

      <Popper
        open={showSuggestions}
        anchorEl={formulaBarRef.current}
        placement='bottom-start'
        disablePortal
        sx={{ zIndex: 2, width: 360, maxWidth: 'calc(100% - 16px)' }}
      >
        <Paper elevation={5} sx={{ maxHeight: 260, overflow: 'auto' }}>
          <Typography variant='caption' color='text.secondary' sx={{ display: 'block', px: 2, pt: 1.25 }}>
            Attributes matching “{activeAttributeSearch?.query || 'all'}”
          </Typography>
          <List dense role='listbox' aria-label='Attribute suggestions'>
            {filteredOptions.map((option, index) => (
              <ListItemButton
                key={option.value}
                selected={index === selectedSuggestionIndex}
                role='option'
                aria-selected={index === selectedSuggestionIndex}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => insertAttribute(option.label)}
              >
                <ListItemIcon sx={{ minWidth: 34, color: '#00B3DC' }}>
                  <ViewColumnOutlinedIcon fontSize='small' />
                </ListItemIcon>
                <ListItemText primary={option.label} secondary={option.persistentName || undefined} />
              </ListItemButton>
            ))}
          </List>
        </Paper>
      </Popper>

      <Stack
        direction='row'
        spacing={1}
        alignItems='flex-start'
        sx={{ bgcolor: '#F8FAFC', border: '1px solid #E3EBF6', borderRadius: 1, p: 1.25 }}
      >
        <InfoOutlinedIcon sx={{ color: '#3B4797', fontSize: 20, mt: 0.1 }} />
        <Box>
          <Typography variant='body2' fontWeight={700}>
            Build the result from left to right
          </Typography>
          <Typography variant='caption' color='text.secondary'>
            Type {'{'} to find an attribute. Everything outside curly brackets is added as text.
          </Typography>
        </Box>
      </Stack>

      <Box>
        <Typography variant='input-label' fontWeight={700}>
          Result preview
        </Typography>
        <Box
          sx={{
            mt: 0.5,
            minHeight: 48,
            display: 'flex',
            alignItems: 'center',
            border: '1px solid #D0D5DD',
            borderRadius: 1,
            bgcolor: '#FCFCFD',
            px: 1.5,
          }}
        >
          <Typography variant='body1' fontWeight={600} color={formulaResult.error ? 'text.secondary' : 'text.primary'}>
            {formulaResult.error ? 'Fix the formula to see its preview.' : formulaResult.preview}
          </Typography>
        </Box>
        {formulaResult.error && (
          <Typography variant='caption' color='error' role='alert' sx={{ display: 'block', mt: 0.5 }}>
            {formulaResult.error}
          </Typography>
        )}
      </Box>
    </Stack>
  );
}

export function ConcatenationSavedParameter({ value }: { value: string }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <Box sx={{ mt: 1.5 }}>
      <Divider />
      <Button
        color='inherit'
        onClick={() => setExpanded((current) => !current)}
        startIcon={expanded ? <ExpandMoreRoundedIcon /> : <ChevronRightRoundedIcon />}
        aria-expanded={expanded}
        sx={{ mt: 0.5, px: 0.5, color: '#3B4797', textTransform: 'none' }}
      >
        Advanced: saved parameter
      </Button>
      <Collapse in={expanded}>
        <Box
          component='code'
          sx={{
            display: 'block',
            mt: 0.5,
            p: 1.25,
            border: '1px solid #D0D5DD',
            borderRadius: 1,
            bgcolor: '#F9FAFB',
            color: '#344054',
            fontSize: 13,
            overflowWrap: 'anywhere',
          }}
        >
          {value || 'No saved parameter yet'}
        </Box>
      </Collapse>
    </Box>
  );
}
